
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import EmailDialog from '@/components/Email/Dialog.vue'
import EmailForm from '@/components/Email/Form.vue'
import { Invoice } from '@/models/accounting/Invoice'
import InvoiceStatus from '@/enums/InvoiceStatus'
import Notification from '@/classes/Notification'
import User from '@/models/User'
import axios from 'axios'
import i18n from '@/i18n'

interface EmailFormRef extends Vue {
	body: string
	subject: string
	send: boolean
	sendCopy: boolean
	receivers: Array<string>
	from: string
	replyTo: string
}

@Component({
	components: { EmailDialog, EmailForm }
})
export default class InvoiceEmailDialog extends Vue {
	@Prop({ required: true })
	show!: boolean

	@Prop({ required: true })
	invoiceId!: string

	@Ref('emailForm')
	emailFormRef!: EmailFormRef

	invoice: Invoice | null = null
	user: User | null = null
	isLoading: boolean = false
	isSending: boolean = false

	async created() {
		this.user = await User.fetchMeLazy()
		await this.loadInvoice()
	}

	@Watch('invoiceId')
	async onInvoiceIdChange() {
		await this.loadInvoice()
	}

	async loadInvoice() {
		if (!this.invoiceId) return
		this.isLoading = true
		try {
			const response = await Invoice.with(['customer']).find(this.invoiceId)
			const inv = response.getData() as Invoice
			inv.sendEmail = true
			this.invoice = inv
		} catch (err) {
			// eslint-disable-next-line
			console.error('Error loading invoice', err)
			await Notification.Error(i18n.t('errors.load_error').toString())
			this.$emit('close')
		} finally {
			this.isLoading = false
		}
	}

	defaultEmailReceiver(invoice: Invoice): string {
		return invoice.getCustomer()?.email ?? ''
	}

	get defaultEmailFrom(): string {
		return this.user?.getCompany()?.name ?? ''
	}

	get defaultEmailReplyTo(): string {
		return this.user?.emailInvoicing ?? ''
	}

	defaultEmailBody(invoice: Invoice): string {
		return i18n.t('emailDialog.invoiceBody', {
			newline: '\n',
			firstName: invoice.getCustomer()?.name ?? '',
			senderFullName: this.user?.getCompany()?.name ?? '',
			senderSignature: this.user?.getCompany()?.emailSignature ?? ''
		}) as string
	}

	defaultEmailSubject(invoice: Invoice): string {
		return i18n.t('emailDialog.invoiceSubject', {
			invoiceNumber: invoice.fullInvoiceNumber ?? invoice.invoiceNumber ?? '',
			invoiceRecipient: this.user?.companyName ?? ''
		}) as string
	}

	async sendEmail() {
		if (!this.invoice) return
		const valid = await this.emailFormRef.$validator.validateAll()
		if (!valid) return

		this.isSending = true
		try {
			if (this.invoice.status !== InvoiceStatus.SENT) {
				await axios.post(
					`${this.invoice.getJsonApiBaseUrl()}${this.invoice.getJsonApiType()}/finalise`,
					// @ts-ignore - serialize is private in Coloquent but needed here (same pattern as Model.ts)
					this.invoice.serialize()
				)
				this.invoice.setStatus(InvoiceStatus.SENT)
			}
			// Read form values back into invoice before sending (mirrors SetsEmailValuesFromForm mixin)
			this.invoice.emailBody = this.emailFormRef.body
			this.invoice.emailSubject = this.emailFormRef.subject
			this.invoice.sendEmail = this.emailFormRef.send
			this.invoice.sendEmailCopy = this.emailFormRef.sendCopy
			this.invoice.receiverEmails = this.emailFormRef.receivers.join(',')
			this.invoice.emailFrom = this.emailFormRef.from
			this.invoice.emailReplyTo = this.emailFormRef.replyTo

			await axios.post(
				`${this.invoice.getJsonApiBaseUrl()}${this.invoice.getJsonApiType()}/send`,
				// @ts-ignore - serialize is private in Coloquent but needed here (same pattern as Model.ts)
				this.invoice.serialize()
			)

			await Notification.Success(i18n.t('invoicing.table.notifications.emailSent.success').toString())
			this.$emit('sent', { invoiceId: this.invoice.getApiId() })
			this.$emit('close')
		} catch (err) {
			// eslint-disable-next-line
			console.error('Error sending invoice email', err)
			await Notification.Error(i18n.t('errors.email_send_error').toString())
		} finally {
			this.isSending = false
		}
	}
}
